import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Icon, Modal, Notice, Panel } from '../design/index.js'
import { useApi, type FeedEntry } from '../app/api.js'
import { ErrorBlock, Loading, messageFor } from './feedback.js'
import { formatDateTime } from './format.js'

/**
 * The Team Board (docs/02 §4a).
 *
 * Two streams in one list: what HAPPENED to the document, read from the audit
 * log, and what people SAID about it. The first is why the second is worth
 * having — "approved · then Ramesh said the rate was wrong" is a sentence you
 * can only read when both are on the same timeline.
 *
 * A remark can carry a file. Those are marked as coming from the board so the
 * drive's Files tab can leave them out of the drawing register.
 */

const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('That file could not be read.'))
    reader.onload = () => {
      const result = String(reader.result ?? '')
      resolve(result.slice(result.indexOf(',') + 1))
    }
    reader.readAsDataURL(file)
  })

export interface TeamBoardProps {
  readonly documentId?: string
  readonly projectId?: string
  /**
   * A setup record instead — a master, a workflow, a print template — named
   * by its kind and id (SET-031, SET-034).
   */
  readonly record?: { readonly type: string; readonly id: string }
  readonly title?: string
}

/** What the Feed dropdown narrows to (PRJ-013). */
type FeedFilter = 'ALL' | 'NOTES' | 'FILES' | 'ACTIVITY'
const FEED_FILTERS: ReadonlyArray<{ key: FeedFilter; label: string; keep: (e: FeedEntry) => boolean }> = [
  { key: 'ALL', label: 'All', keep: () => true },
  { key: 'NOTES', label: 'Notes', keep: (e) => e.kind === 'REMARK' && (e.body ?? '') !== '' },
  { key: 'FILES', label: 'Files', keep: (e) => e.attachment !== null },
  { key: 'ACTIVITY', label: 'Activity', keep: (e) => e.kind === 'EVENT' },
]

/**
 * The Team Board as the reference lays it out (PRJ-013): `+ Add note`,
 * `Upload file` and a `Feed (n)` dropdown — all, notes, files or activity —
 * with an expand control that opens the feed full-size; "No feed available"
 * when there is nothing to read.
 */
export const TeamBoard = ({ documentId, projectId, record, title = 'Team board' }: TeamBoardProps) => {
  const api = useApi()
  const client = useQueryClient()
  const picker = useRef<HTMLInputElement | null>(null)
  const subject = {
    ...(documentId ? { documentId } : {}),
    ...(projectId ? { projectId } : {}),
    ...(record ? { recordType: record.type, recordId: record.id } : {}),
  }

  const key = ['board', api.companyId, documentId ?? '', projectId ?? '', record?.type ?? '', record?.id ?? '']
  const feed = useQuery({
    queryKey: key,
    queryFn: () => api.board(subject),
    enabled: Boolean(documentId || projectId || record),
  })

  const [body, setBody] = useState('')
  const [composing, setComposing] = useState(false)
  const [pending, setPending] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<FeedFilter>('ALL')
  const [expanded, setExpanded] = useState(false)

  const post = useMutation({
    mutationFn: async (input: { body: string; file: File | null }) => api.postToBoard({
      ...subject,
      body: input.body,
      ...(input.file
        ? { file: { fileName: input.file.name, contentType: input.file.type, base64: await readAsBase64(input.file) } }
        : {}),
    }),
    onSuccess: (rows) => {
      setError(null)
      setBody('')
      setPending(null)
      setComposing(false)
      client.setQueryData(key, rows)
      // A board attachment lands in the drive too.
      void client.invalidateQueries({ queryKey: ['drive'] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.removeRemark(id.replace(/^remark:/, '')),
    onSuccess: () => { void client.invalidateQueries({ queryKey: key }) },
    onError: (err) => setError(messageFor(err)),
  })

  if (feed.isLoading) return <Loading />
  if (feed.error) return <ErrorBlock error={feed.error} />

  const entries = feed.data ?? []
  const chosen = FEED_FILTERS.find((x) => x.key === filter) ?? FEED_FILTERS[0]!
  const shown = entries.filter(chosen.keep)

  const list = shown.length === 0 ? (
    <p className="helper" style={{ marginTop: 12 }}>No feed available</p>
  ) : (
    <ol className="board">
      {shown.map((entry) => <BoardRow key={entry.id} entry={entry} onRemove={remove.mutate} api={api} />)}
    </ol>
  )

  return (
    <Panel
      title={title}
      aside={
        <button type="button" className="icon-button" aria-label="Expand the team board" onClick={() => setExpanded(true)}>
          <Icon name="updown" size={18} />
        </button>
      }
    >
      {error ? <Notice kind="bad">{error}</Notice> : null}

      <div className="row row--wrap" style={{ gap: 8 }}>
        <Button kind="ghost" icon={<Icon name="plus" size={18} />} onClick={() => setComposing((v) => !v)}>
          Add note
        </Button>
        <input
          ref={picker}
          type="file"
          hidden
          data-testid="board-file"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0]
            event.currentTarget.value = ''
            if (!file) return
            // Composing a note: the file goes with it. Otherwise it is posted
            // on its own, which is what Upload File means.
            if (composing) setPending(file)
            else post.mutate({ body: '', file })
          }}
        />
        <Button kind="ghost" icon={<Icon name="clip" size={18} />} disabled={post.isPending} onClick={() => picker.current?.click()}>
          {composing ? (pending ? `Attached: ${pending.name}` : 'Attach a file') : 'Upload file'}
        </Button>
        <label className="row" style={{ marginLeft: 'auto', gap: 8 }}>
          <span className="label">Feed ({entries.length})</span>
          <select
            className="control"
            style={{ width: 'auto' }}
            aria-label="Show in the feed"
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value as FeedFilter)}
          >
            {FEED_FILTERS.map((x) => (
              <option key={x.key} value={x.key}>{x.label} ({entries.filter(x.keep).length})</option>
            ))}
          </select>
        </label>
      </div>

      {composing ? (
        <div className="board-compose" style={{ marginTop: 12 }}>
          <textarea
            className="control"
            style={{ minHeight: 76 }}
            aria-label="Note"
            placeholder="Say something about this — a question, a decision, why a rate is what it is."
            value={body}
            onChange={(event) => setBody(event.currentTarget.value)}
          />
          <div className="row row--wrap" style={{ marginTop: 8 }}>
            {pending ? <Button kind="ghost" onClick={() => setPending(null)}>Remove the file</Button> : null}
            <Button kind="ghost" onClick={() => { setComposing(false); setBody(''); setPending(null) }}>Cancel</Button>
            <Button
              kind="primary"
              disabled={post.isPending || (body.trim() === '' && pending === null)}
              onClick={() => post.mutate({ body, file: pending })}
            >
              Post
            </Button>
          </div>
        </div>
      ) : null}

      {list}

      {expanded ? (
        <Modal
          title={`${title} — ${chosen.label.toLowerCase()} (${shown.length})`}
          wide
          onClose={() => setExpanded(false)}
          actions={<Button kind="ghost" onClick={() => setExpanded(false)}>Close</Button>}
        >
          {list}
        </Modal>
      ) : null}
    </Panel>
  )
}

const BoardRow = ({
  entry, onRemove, api,
}: {
  entry: FeedEntry
  onRemove: (id: string) => void
  api: ReturnType<typeof useApi>
}) => (
  <li className={entry.kind === 'REMARK' ? 'board__item board__item--remark' : 'board__item'}>
    <span className="board__mark" aria-hidden="true">
      <Icon name={entry.kind === 'REMARK' ? 'info' : 'check'} size={16} />
    </span>
    <div className="board__body">
      <div className="board__head">
        <span className="board__who">{entry.text}</span>
        <span className="board__when">{formatDateTime(entry.at)}</span>
      </div>
      {entry.body ? <p className="board__text">{entry.body}</p> : null}
      {entry.attachment ? (
        <a className="board__file" href={api.driveFileUrl(entry.attachment.id)}>
          <Icon name="clip" size={15} /> {entry.attachment.fileName}
        </a>
      ) : null}
      {entry.mine ? (
        <button type="button" className="linkish" onClick={() => onRemove(entry.id)}>
          Withdraw
        </button>
      ) : null}
    </div>
  </li>
)
